import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ServiceDetail from './components/ServiceDetail';
import ProcessSteps from './components/ProcessSteps';
import WhyUs from './components/WhyUs';
import QuoteForm from './components/QuoteForm';
import { serviceCategories } from '../../mocks/services';

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

export default function ServicesPage() {
  const location = useLocation();

  useEffect(() => {
    // Inject Schema.org JSON-LD for ServicesPage
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yanginalgila.com';
    
    const schemaService = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Yangın Algılama ve Alarm Sistemleri",
      "provider": {
        "@type": "LocalBusiness",
        "name": "YANGINALGILA.COM",
        "url": siteUrl,
        "telephone": "+90-312-XXX-XXXX",
        "email": "info@yanginalgila.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Ankara",
          "addressRegion": "Ankara",
          "addressCountry": "TR"
        }
      },
      "areaServed": {
        "@type": "Country",
        "name": "Turkey"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Yangın Güvenlik Hizmetleri",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Yangın Algılama Sistemleri",
              "description": "Modern duman ve ısı sensörleri ile erken uyarı sistemleri kurulumu"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Yangın Alarm Sistemleri",
              "description": "Sesli ve görsel alarm sistemleri kurulumu ve entegrasyonu"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Bakım ve Onarım",
              "description": "Periyodik bakım, test ve onarım hizmetleri"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Sistem Modernizasyonu",
              "description": "Mevcut sistemlerin güncellenmesi ve yenilenmesi"
            }
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaService);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    document.title = 'Yangın Algılama Hizmetleri | Keşif, Kurulum, Bakım | yangınalgıla.com';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Yangın algılama sistemi keşif, kurulum ve bakım hizmetleri. Profesyonel yangın algılama projelendirme, duman dedektörü montajı, alarm sistemi kurulumu. Ankara merkezli, Türkiye genelinde hizmet. Ücretsiz keşif ve teklif alın.');
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) metaKeywords.setAttribute('content', 'yangın algılama kurulum, yangın algılama bakım, yangın algılama projelendirme, duman dedektörü kurulumu, yangın alarm sistemi montajı');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://yanginalgila.com/hizmetler');
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace('#', ''));
      if (el) {
        // Delay to ensure the element is rendered before scrolling
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar />

      {/* Hero Section */}
      <header className="relative pt-28 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/1b893072ea789b6311b123988509fc11.jpg"
            alt="Yangın Algılama Hizmetleri"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10 pt-16 pb-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center w-full"
          >
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-5 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-8"
            >
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-white">
                Profesyonel Yangın Güvenlik Çözümleri
              </span>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="text-6xl md:text-7xl font-bold text-white leading-tight mb-6"
            >
              HİZMETLERİMİZ
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-lg text-white/80 max-w-3xl mx-auto mb-10 leading-relaxed"
            >
              Yangın algılama, duman sensörleri ve alarm sistemleri ile can ve mal
              güvenliğinizi en üst seviyede koruyoruz. CE, TSE ve ISO 9001 sertifikalı
              çözümlerimizi keşfedin.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="flex flex-wrap justify-center gap-4"
            >
              {serviceCategories.map((cat) => (
                <a
                  key={cat.id}
                  href={`#${cat.id}`}
                  className="px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium rounded-full hover:bg-red-600 hover:border-red-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className={`${cat.icon} mr-2`}></i>
                  {cat.title}
                </a>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="max-w-4xl mx-auto relative z-10"
        >
          <div className="bg-white rounded-2xl shadow-xl p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '2350+', label: 'Toplam Kurulum', icon: 'ri-building-2-line' },
              { value: '15+', label: 'Yıllık Deneyim', icon: 'ri-time-line' },
              { value: '%99.8', label: 'Müşteri Memnuniyeti', icon: 'ri-heart-line' },
              { value: '7/24', label: 'Teknik Destek', icon: 'ri-customer-service-line' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-xl mx-auto mb-2">
                  <i className={`${stat.icon} text-lg text-red-600`}></i>
                </div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </header>

      {/* Service Details */}
      {serviceCategories.map((service, index) => (
        <div
          key={service.id}
          className={index % 2 === 1 ? 'bg-gray-50' : 'bg-white'}
        >
          <ServiceDetail
            id={service.id}
            title={service.title}
            subtitle={service.subtitle}
            description={service.description}
            icon={service.icon}
            image={service.image}
            features={service.features}
            stats={service.stats}
            reversed={index % 2 === 1}
          />
        </div>
      ))}

      {/* Process Steps */}
      <ProcessSteps />

      {/* Why Us */}
      <WhyUs />

      {/* CTA Banner */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl overflow-hidden"
          >
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20dark%20background%20with%20glowing%20red%20and%20orange%20fire%20particles%20and%20sparks%2C%20dramatic%20cinematic%20lighting%2C%20dark%20moody%20atmosphere%20with%20warm%20ember%20glow%20effects%2C%20professional%20safety%20themed%20abstract%20art%2C%20deep%20shadows%20with%20vibrant%20fire%20accents&width=1200&height=400&seq=svc-cta1&orientation=landscape"
                alt="Yangın Güvenlik Çözümleri"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/60 to-black/40"></div>
            </div>
            <div className="relative z-10 p-12 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Güvenliğiniz İçin Harekete Geçin
                </h2>
                <p className="text-white/70 text-lg">
                  Ücretsiz keşif ve teklif için hemen iletişime geçin.
                </p>
              </div>
              <div className="flex gap-4">
                <a
                  href="#teklif-al"
                  className="px-8 py-4 bg-red-600 text-white font-semibold rounded-full hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                >
                  <i className="ri-file-list-3-line"></i> Teklif Alın
                </a>
                <a
                  href="tel:+908501234567"
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                >
                  <i className="ri-phone-line"></i> Bizi Arayın
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quote Form */}
      <QuoteForm />

      {/* Footer */}
      <Footer />
    </div>
  );
}
